import {
  ActionReducerMap,
  createFeatureSelector,
  createSelector
} from '@ngrx/store';
import {EligibilityAndAvailabilityDTO} from '@staff-app/models/EligibilityAndAvailabilityDTO';
import * as fromContactInfo from '@staff-app/reducers/contact-info.reducer';
import * as fromEducation from '@staff-app/reducers/education.reducer';
import * as fromEligibilityAndAvailability from '@staff-app/reducers/eligibility-and-availability.reducer';
import * as fromExperience from '@staff-app/reducers/experience.reducer';
import * as fromMetaInfo from '@staff-app/reducers/meta-info.reducer';
import * as fromLocation from '@staff-app/reducers/location.reducer';
import * as fromPortfolio from '@staff-app/reducers/portfolio.reducer';
import * as fromPosition from '@staff-app/reducers/position.reducer';
import * as fromRoot from '@store/reducers';

export interface StaffAppState {
  metaInfo: fromMetaInfo.State;
  locations: fromLocation.State;
  positionsState: fromPosition.State;
  contactInfo: fromContactInfo.State;
  eligibilityAndAvailability: EligibilityAndAvailabilityDTO;
  experiences: fromExperience.State;
  education: fromEducation.State;
  workHistoryAndPortfolio: fromPortfolio.State;
}
export interface State extends fromRoot.State {
  staffApp: StaffAppState;
}
export const reducers: ActionReducerMap<StaffAppState> = {
  metaInfo: fromMetaInfo.reducer,
  locations: fromLocation.reducer,
  positionsState: fromPosition.reducer,
  contactInfo: fromContactInfo.reducer,
  eligibilityAndAvailability: fromEligibilityAndAvailability.reducer,
  experiences: fromExperience.reducer,
  education: fromEducation.reducer,
  workHistoryAndPortfolio: fromPortfolio.reducer
};

export const getStaffAppState = createFeatureSelector<StaffAppState>(
  'staff-app'
);

export const getLocationState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.locations
);
export const getMetaInfoState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.metaInfo
);
export const getPositionState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.positionsState
);
export const getContactInfoState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.contactInfo
);
export const getEligibilityAndAvailabilityState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.eligibilityAndAvailability
);
export const getExperienceState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.experiences
);
export const getEducationState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.education
);
export const getWorkHistoryAndPortfolioState = createSelector(
  getStaffAppState,
  (state: StaffAppState) => state.workHistoryAndPortfolio
);

export const getHasSeenIntro = createSelector(
  getMetaInfoState,
  fromMetaInfo.getHasSeenIntro
);
export const getJobAppOpenStatus = createSelector(
  getMetaInfoState,
  fromMetaInfo.getJobAppOpenStatus
);
export const getJobAppProcessStatus = createSelector(
  getMetaInfoState,
  fromMetaInfo.getJobAppProcessStatus
);
export const getIsJobAppCompleted = createSelector(
  getMetaInfoState,
  fromMetaInfo.getIsJobAppCompleted
);
export const getIsJobAppEditable = createSelector(
  getMetaInfoState,
  fromMetaInfo.getIsJobAppEditable
);
export const getRafCode = createSelector(getMetaInfoState, fromMetaInfo.getRafCode);
export const getContactInfo = createSelector(
  getContactInfoState,
  fromContactInfo.getContactInfo
);

export const getAvailablePositions = createSelector(
  getPositionState,
  fromPosition.getAvailablePositions
);

export const isPositionsLoadedFromDB = createSelector(
  getPositionState,
  fromPosition.isPositionsLoadedFromDB
);

export const getSelectedPositions = createSelector(
  getPositionState,
  fromPosition.getSelectedPositions
);

export const getWillWorkAnywhere = createSelector(
  getLocationState,
  fromLocation.getWillWorkAnywhere
);

export const isLocationsLoadedFromDB = createSelector(
  getLocationState,
  fromLocation.isLocationsLoadedFromDB
);

export const getSelectedLocationIds = createSelector(
  getLocationState,
  fromLocation.getSelectedLocations
);
export const getAvailableLocations = createSelector(
  getLocationState,
  fromLocation.selectAll
);

export const getEligibilityAndAvailability = createSelector(
  getEligibilityAndAvailabilityState,
  fromEligibilityAndAvailability.getEligibilityAndAvailability
);

export const getManagementSkills = createSelector(
  getExperienceState,
  fromExperience.getManagementSkills
);
export const getInstructorSkills = createSelector(
  getExperienceState,
  fromExperience.getInstructorSkills
);
export const getProductSkills = createSelector(
  getExperienceState,
  fromExperience.getProductSkills
);
export const getExperienceData = createSelector(
  getExperienceState,
  fromExperience.getExperienceData
);

export const getEducations = createSelector(
  getEducationState,
  fromEducation.getEducations
);
export const getWorkHistoryAndPortfolio = createSelector(
  getWorkHistoryAndPortfolioState,
  fromPortfolio.getWorkHistoryAndPortfolio
);
export const getPortfolioFiles = createSelector(
  getWorkHistoryAndPortfolioState,
  fromPortfolio.getPortfolioFiles
);
